import { PlusCircleIcon, TrashIcon } from "@heroicons/react/outline";
import { zodResolver } from "@hookform/resolvers/zod";
import { HttpError, useOne } from "@refinedev/core";
import Button from "components/button"
import Input from "components/input";
import { Label } from "components/label";
import { IModalProps } from "components/modal"
import { RightSideModal } from "components/rightSideModal"
import Select from "components/select";
import { SwitchButton } from "components/switchButton";
import { TextEditor } from "components/textEditor";
import { IMultilingualContent, ISimpleEventDetail, PriceType, ProductType, SupportedLanguage, IAttractionShowtimeTicketForm, ShowTime } from "interfaces"
import { get } from "lodash";
import { useEffect, useState } from "react";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { LANGUAGES, PRICE_TYPES, SELLING_TYPE, WEEK_DAYS } from "utills/constants";
import { z } from "zod";
import { ParentTicketSchema, SubTicketSchema } from "../schema";
import DateTimePicker from "components/datetimePicker";
import moment from "moment";
import { arrayCompare } from "utills/helpers";
import TimePicker from "components/timePicker";

interface TicketFormProps extends IModalProps {
  eventDetailData: ISimpleEventDetail;
  showTimes: ShowTime[];
  onSubmit: (data: IAttractionShowtimeTicketForm) => void;
  productType: ProductType;
}

interface IAttendantTemplateField {
  id: string;
  template_id: string;
  name: IMultilingualContent;
}

const PhaseTicketSchema = (eventDetailData: ISimpleEventDetail) => {
  return z.object({
    ticket: ParentTicketSchema(eventDetailData),
    sub_ticket: SubTicketSchema(eventDetailData),
  })
};

const DEFAULLT_TEMPLATE = [{
  template_id: "",
  name: {
    en: "",
    vi: ""
  }
}];

const DEFAULT_TICKET_VALUE = {
  ticket: {
    category: {
      en: "",
      vi: ""
    },
    selling_min_qty: 1,
    selling_max_qty: 10,
    selling_type: "multiple",
    is_package: false,
    attendant_template: DEFAULLT_TEMPLATE,
  },
  sub_ticket: {
    type_name: {
      en: "",
      vi: ""
    },
    short_description: {
      en: "",
      vi: ""
    },
    price_type: PriceType.PAID,
    original_price: 0,
    price: 0,
    sell_from: moment().startOf("day").format("YYYY-MM-DD HH:mm:ss"),
    sell_to: moment().endOf("day").format("YYYY-MM-DD HH:mm:ss"),
    is_soldout: false,
    is_active: true,
    showtime: "",
    hide_in_ui: false,
    selling_days: [],
    weekly_event_days: [],
    sold_out_before: "",
    detailed_ticket_information: {
      en: "",
      vi: ""
    }
  }
}

enum PhaseTicketFormStep {
  PARENT_TICKET = "ticket",
  SUB_TICKET = "sub_ticket"
}

const ALL_OPTION = {label: "All", value: "all"};

export const TicketForm = (props: TicketFormProps) => {
  const [ticketFormStep, setTicketFormStep] = useState<PhaseTicketFormStep>(PhaseTicketFormStep.PARENT_TICKET);
  const {open, onClose, eventDetailData, showTimes, productType} = props;

  const { data } = useOne({
    resource: `content/attendant-templates/events`,
    id: eventDetailData.id,
    meta: {
      resource: "data",
    },
    queryOptions: {
      enabled: !!eventDetailData.id,
    } as any,
  });

  const templatesDataArr = (data?.data ?? []).map((template: any) => ({
    label: get(template, "attendant_template_name", ""),
    value: get(template, "id", "")
  }))

  const {
    control,
    handleSubmit,
    register,
    watch,
    trigger,
    reset,
    setValue,
    formState: { errors },
  } = useForm<IAttractionShowtimeTicketForm, HttpError, IAttractionShowtimeTicketForm>({
    defaultValues: {
      ...DEFAULT_TICKET_VALUE,
      ...props.eventDetailData.date_pricing?.enabled ? {
        sub_ticket: {
          ...DEFAULT_TICKET_VALUE.sub_ticket,
          pricing_by_date_type: {
            weekend: {
              original_price: 0,
              price: 0,
            },
            holiday: {
              original_price: 0,
              price: 0,
            }
          }
        }
      } : {}
    },
    resolver: (values, context, options) => {
      const createResolver = zodResolver(
        PhaseTicketSchema(eventDetailData)
      );
      return createResolver(values, context, options);
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "ticket.attendant_template"
  });

  useEffect(() => {
    if (open) {
      setTicketFormStep(PhaseTicketFormStep.PARENT_TICKET);
      reset({...DEFAULT_TICKET_VALUE});
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const _handleSubmitParentTicket = () => {
    trigger("ticket").then((isValid) => {
      if (isValid) {
        setTicketFormStep(PhaseTicketFormStep.SUB_TICKET);
      }
    });
  };

  const onSubmit = (data: IAttractionShowtimeTicketForm) => {
    const submitData : IAttractionShowtimeTicketForm = {
      ticket: {
        ...data.ticket,
        product_type: productType
      },
      sub_ticket: data.sub_ticket,
    }
    props.onSubmit(submitData);
  }

  const _submit = () => {
    if (ticketFormStep === PhaseTicketFormStep.PARENT_TICKET) {
      _handleSubmitParentTicket();
    } else {
      handleSubmit(onSubmit)();
    }
  }

  const ticketData = watch("ticket");

  const firstLanguage = eventDetailData.languages.includes(SupportedLanguage.EN) ? "en" : eventDetailData.languages[0] ?? 'vi';

  const showtimeArr = showTimes.map(showtime => ({
    label: get(showtime, "time", ""),
    value: get(showtime, "id", "")
  }))

  const weekdayArr = ([ALL_OPTION]).concat(WEEK_DAYS);

  console.log(errors);
  
  return (
    <RightSideModal title="Add Ticket" closeOnClickBackdrop={false} open={open} onClose={onClose} footer={(
      <div className="w-full flex flex-row gap-x-2 items-center">
        <Button 
          className="w-full bg-white border-none text-brand-500 ring-1 ring-inset ring-brand-500 hover:bg-gray-50 active:bg-gray-100" 
          onClick={() =>ticketFormStep === PhaseTicketFormStep.PARENT_TICKET ? onClose() : setTicketFormStep(PhaseTicketFormStep.PARENT_TICKET)}
        >
          {ticketFormStep === PhaseTicketFormStep.PARENT_TICKET ? "Cancel" : "Back"}
        </Button>
        <Button className="w-full uppercase" onClick={_submit}>
          {ticketFormStep === PhaseTicketFormStep.PARENT_TICKET ? "Next & Setup sub-ticket" : "Save"}
        </Button>
      </div>
    )}>
      <div className="p-4 sm:px-6">
        {ticketFormStep === PhaseTicketFormStep.PARENT_TICKET ? (
          <div className="grid sm:grid-cols-6 gap-y-6 gap-x-4">
            <div className="col-span-full pb-2 border-b-[1px] border-b-gray-200">
              <p className="text-sm">Ticket Info</p>
            </div>
            <div className="col-span-full">
              <Label
                required
                id={`category_${eventDetailData.languages[0] ?? "en"}`}
                name="Category"
              />
              <div className="flex flex-col gap-y-4">
                {eventDetailData.languages.map((lang) => (
                  <div key={lang}>
                    <div className="relative" key={lang}>
                      <Input
                        id={`category_${lang}`}
                        type="text"
                        className="pr-11"
                        placeholder={`Cateogry (${lang})`}
                        {...register(`ticket.category.${lang}`)}
                        error={get(
                          errors,
                          `ticket.category.${lang}.message`,
                          ""
                        )}
                      />
                      <img
                        src={`/images/flags/circle-${lang}.png`}
                        alt="Language Icon"
                        className="absolute top-1/2 right-3 -translate-y-1/2"
                      />
                    </div>
                    {get(
                      errors,
                      `ticket.category.${lang}`,
                      ""
                    ) ? (
                      <p className="error-text">
                        <span className="font-medium">
                          {get(
                            errors,
                            `ticket.category.${lang}.message`,
                            ""
                          )}
                        </span>
                      </p>
                    ) : null}
                  </div>
                ))}
              </div>
            </div>
            <div className="sm:col-span-3">
              <Label
                id="selling_min_qty"
                name="Select min quantity"
                required
              />
              <Input
                id="selling_min_qty"
                type="number"
                min={0}
                placeholder={`Select min quantity`}
                {...register(`ticket.selling_min_qty`, {
                  valueAsNumber: true
                })}
                error={get(
                  errors,
                  `ticket.selling_min_qty.message`,
                  ""
                )}
              />
              {get(
                errors,
                `ticket.selling_min_qty`,
                ""
              ) ? (
                <p className="error-text">
                  <span className="font-medium">
                    {get(
                      errors,
                      `ticket.selling_min_qty.message`,
                      ""
                    )}
                  </span>
                </p>
              ) : null}
            </div>
            <div className="sm:col-span-3">
              <Label
                id="selling_max_qty"
                name="Select max quantity"
                required
              />
              <Input
                id="selling_max_qty"
                type="number"
                min={0}
                placeholder={`Select max quantity`}
                {...register(`ticket.selling_max_qty`, {
                  valueAsNumber: true
                })}
                error={get(
                  errors,
                  `ticket.selling_max_qty.message`,
                  ""
                )}
              />
              {get(
                errors,
                `ticket.selling_max_qty`,
                ""
              ) ? (
                <p className="error-text">
                  <span className="font-medium">
                    {get(
                      errors,
                      `ticket.selling_max_qty.message`,
                      ""
                    )}
                  </span>
                </p>
              ) : null}
            </div>
            <div className="col-span-full">
              <Label
                id="selling_type"
                name="Selling Type"
                required
              />
              <Controller
                control={control}
                name="ticket.selling_type"
                render={({ field: { onChange, value = "", ref } }) => {
                  return (
                    <Select
                      key="selling_type"
                      inputRef={ref}
                      options={SELLING_TYPE}
                      id="selling_type"
                      placeholder="Select Selling Type"
                      defaultValue={
                        SELLING_TYPE.find(
                          (selected: { label: string; value: string }) =>
                            selected.value === value
                        ) ?? ""
                      }
                      value={SELLING_TYPE.find(
                        (selected: { label: string; value: string }) =>
                          selected.value === value
                      )}
                      onChange={(selected: { label: string; value: string }) => {
                        onChange(selected?.value);
                      }}
                      error={get(errors, `ticket.selling_type`, "")}
                    />
                  );
                }}
              />
              {get(
                errors,
                `ticket.selling_type`,
                ""
              ) ? (
                <p className="error-text">
                  <span className="font-medium">
                    {get(
                      errors,
                      `ticket.selling_type.message`,
                      ""
                    )}
                  </span>
                </p>
              ) : null}
            </div>
            <div className="col-span-full pb-2 border-b-[1px] border-b-gray-200">
              <p className="text-sm">Participant form Info</p>
            </div>
            <div className="col-span-full">
              {watch("ticket.is_package") ? (
                <button
                  type="button"
                  className="flex flex-row items-center w-fit text-sm bg-white text-brand-500 active:bg-gray-50"
                  onClick={() => append(DEFAULLT_TEMPLATE)}
                >
                  <PlusCircleIcon className="w-5 h-5 mr-1.5" />{" "}
                  <span className="shrink-0 text-sm">Add Participant forms</span>
                </button>
              ) : <></>}
              <div className="divide-y divide-gray-300">
                {fields.map((field: IAttendantTemplateField, index: number) => (
                  <div key={field.id} className="py-4">
                    {(watch("ticket.is_package") && fields.length > 1) ? (
                      <div className="col-span-full flex justify-end">
                        <button onClick={() => remove(index)}>
                          <TrashIcon className="w-6 h-6 text-brand-500" />
                        </button>
                      </div>
                    ) : <></>}
                    <div>
                      <Label
                        id={`attendant_template.${index}.template_id`}
                        name="Select Participant Form"
                        required
                      />
                      <Controller
                        control={control}
                        name={`ticket.attendant_template.${index}.template_id`}
                        render={({ field: { onChange, value = "", ref } }) => {
                          return (
                            <Select
                              key={`attendant_template.${index}.template_id`}
                              inputRef={ref}
                              options={templatesDataArr}
                              id="template_id"
                              placeholder="Select Template"
                              defaultValue={
                                templatesDataArr.find(
                                  (selected: { label: string; value: string }) =>
                                    selected.value === value
                                ) ?? ""
                              }
                              value={templatesDataArr.find(
                                (selected: { label: string; value: string }) =>
                                  selected.value === value
                              )}
                              onChange={(selected: { label: string; value: string }) => {
                                onChange(selected?.value);
                              }}
                              error={get(errors, `ticket.attendant_template.${index}.template_id`, "")}
                            />
                          );
                        }}
                      />
                      {get(
                        errors,
                        `ticket.attendant_template.${index}.template_id`,
                        ""
                      ) ? (
                        <p className="error-text">
                          <span className="font-medium">
                            {get(
                              errors,
                              `ticket.attendant_template.${index}.template_id.message`,
                              ""
                            )}
                          </span>
                        </p>
                      ) : null}
                    </div>
                    {watch("ticket.is_package") ? (
                      <div className="mt-4">
                        <Label
                          id={`attendant_template.${index}.name`}
                          name="Form name displayed"
                        />
                        <div className="flex flex-col gap-y-4">
                          {eventDetailData.languages.map((lang) => (
                            <div key={lang}>
                              <div className="relative">
                                <Input
                                  id={`attendant_template.${index}.name.${lang}`}
                                  type="text"
                                  className="pr-11"
                                  placeholder={`Form name displayed (${lang})`}
                                  {...register(`ticket.attendant_template.${index}.name.${lang}`)}
                                  error={get(
                                    errors,
                                    `ticket.attendant_template.${index}.name.${lang}.message`,
                                    ""
                                  )}
                                />
                                <img
                                  src={`/images/flags/circle-${lang}.png`}
                                  alt="Language Icon"
                                  className="absolute top-1/2 right-3 -translate-y-1/2"
                                />
                              </div>
                              {get(
                                errors,
                                `ticket.attendant_template.${index}.name.${lang}`,
                                ""
                              ) ? (
                                <p className="error-text">
                                  <span className="font-medium">
                                    {get(
                                      errors,
                                      `ticket.attendant_template.${index}.name.${lang}.message`,
                                      ""
                                    )}
                                  </span>
                                </p>
                              ) : null}
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : <></>}
                  </div>
                ))}
                {get(
                  errors,
                  `ticket.attendant_template`,
                  ""
                ) ? (
                  <p className="error-text">
                    <span className="font-medium">
                      {get(
                        errors,
                        `ticket.attendant_template.message`,
                        ""
                      )}
                    </span>
                  </p>
                ) : null}
              </div>
            </div>
          </div>
        ) : <></>}
        {ticketFormStep === PhaseTicketFormStep.SUB_TICKET ? (
          <div className="grid sm:grid-cols-6 gap-y-6 gap-x-4">
            <div className="col-span-full">
              <Controller
                control={control}
                name="sub_ticket.price_type"
                render={({ field: { onChange, value = "", ref } }) => {
                  return (
                    <div className="flex flex-row flex-wrap gap-x-3">
                      {PRICE_TYPES.map(type => (
                        <button
                          key={type.value}
                          type="button"
                          className={`rounded-md ${type.value === value ? "bg-[#A7B4FE] hover:bg-indigo-300 focus-visible:outline-indigo-300" : "bg-white ring-1 ring-inset ring-gray-300 hover:bg-gray-50"} px-6 py-3 text-sm font-semibold text-brand-500 shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2`}
                          onClick={() => {
                            onChange(type.value)
                            if (type.value === PriceType.FREE) {
                              setValue("sub_ticket.original_price", 0, {shouldValidate: true});
                              setValue("sub_ticket.price", 0, {shouldValidate: true});
                              if (eventDetailData?.date_pricing?.enabled) {
                                setValue("sub_ticket.pricing_by_date_type.holiday.original_price", 0, {shouldValidate: true});
                                setValue("sub_ticket.pricing_by_date_type.holiday.price", 0, {shouldValidate: true});
                                setValue("sub_ticket.pricing_by_date_type.weekend.original_price", 0, {shouldValidate: true});
                                setValue("sub_ticket.pricing_by_date_type.weekend.price", 0, {shouldValidate: true});
                              }
                            } 
                          }}
                        >
                          {type.label}
                        </button>
                      
                      ))}
                    </div>
                  );
                }}
              />
              {get(
                errors,
                `sub_ticket.price_type`,
                ""
              ) ? (
                <p className="error-text">
                  <span className="font-medium">
                    {get(
                      errors,
                      `sub_ticket.price_type.message`,
                      ""
                    )}
                  </span>
                </p>
              ) : null}
            </div>

            <div className="col-span-full pb-2 border-b-[1px] border-b-gray-200">
              <p className="text-sm">Sub-ticket Info</p>
            </div>
            <div className="col-span-full flex items-center justify-between font-medium">
              <p className="text-sm">Category</p>
              <p>
                {get(
                  ticketData, 
                  `category.${firstLanguage}`
                )}
              </p>
            </div>

            <div className="col-span-full">
              <Label
                id="showtime"
                name="Showtime"
                required
              />
              <Controller
                control={control}
                name="sub_ticket.showtime"
                render={({ field: { onChange, value = "", ref } }) => {
                  return (
                    <Select
                      key="showtime"
                      inputRef={ref}
                      options={showtimeArr}
                      id="sub_ticket.showtime"
                      placeholder="Select showtime"
                      defaultValue={
                        showtimeArr.find(
                          (selected: { label: string; value: string }) =>
                            selected.value === value
                        ) ?? ""
                      }
                      value={showtimeArr.find(
                        (selected: { label: string; value: string }) =>
                          selected.value === value
                      )}
                      onChange={(selected: { label: string; value: string }) => {
                        onChange(selected?.value);
                      }}
                      error={get(errors, `sub_ticket.showtime`, "")}
                    />
                  );
                }}
              />
              {get(errors, `sub_ticket.showtime`, "") ? (
                <p className="error-text">
                  <span className="font-medium">
                    {get(
                      errors,
                      `sub_ticket.showtime.message`,
                      ""
                    )}
                  </span>
                </p>
              ) : null}
            </div>

            <div className="col-span-full">
              <Label
                required
                id={`type_name_${eventDetailData.languages[0] ?? "en"}`}
                name="Ticket name displayed"
              />
              <div className="flex flex-col gap-y-4">
                {eventDetailData.languages.map((lang) => (
                  <div key={lang}>
                    <div className="relative" key={lang}>
                      <Input
                        id={`type_name_${lang}`}
                        type="text"
                        className="pr-11"
                        placeholder={`Ticket name displayed (${lang})`}
                        {...register(`sub_ticket.type_name.${lang}`)}
                        error={get(
                          errors,
                          `sub_ticket.type_name.${lang}.message`,
                          ""
                        )}
                      />
                      <img
                        src={`/images/flags/circle-${lang}.png`}
                        alt="Language Icon"
                        className="absolute top-1/2 right-3 -translate-y-1/2"
                      />
                    </div>
                    {get(
                      errors,
                      `sub_ticket.type_name.${lang}`,
                      ""
                    ) ? (
                      <p className="error-text">
                        <span className="font-medium">
                          {get(
                            errors,
                            `sub_ticket.type_name.${lang}.message`,
                            ""
                          )}
                        </span>
                      </p>
                    ) : null}
                  </div>
                ))}
              </div>
            </div>
            <div className="col-span-full">
              <Label
                id={`short_description_${eventDetailData.languages[0] ?? "en"}`}
                name="Note below Ticket name"
              />
              <div className="flex flex-col gap-y-4">
                {eventDetailData.languages.map(
                  (lang: SupportedLanguage) => (
                    <div key={lang}>
                      <div
                        className={`w-fit flex flex-row items-center gap-x-1 p-2 -mb-1 rounded-tl-md rounded-tr-md border-magnolia-600 border-solid border-[1px] bg-magnolia-400 font-medium text-sm`}
                      >
                        <img
                          src={`/images/flags/circle-${lang}.png`}
                          alt="Language Icon"
                          className="w-4 shrink-0"
                        />
                        <p>
                          {
                            LANGUAGES.find(
                              (language) =>
                                language.value === lang
                            )?.label
                          }
                        </p>
                      </div>

                      <Controller
                        control={control}
                        name={`sub_ticket.short_description.${lang}`}
                        render={({
                          field: { onChange, value = "" },
                        }) => (
                          <TextEditor
                            key={`short_description.${lang}`}
                            value={value ?? ""}
                            onChange={onChange}
                          />
                        )}
                      />

                      {get(
                        errors,
                        `sub_ticket.short_description.${lang}`,
                        ""
                      ) ? (
                        <p className="error-text">
                          <span className="font-medium">
                            {get(
                              errors,
                              `sub_ticket.short_description.${lang}.message`,
                              ""
                            )}
                          </span>
                        </p>
                      ) : null}
                    </div>
                  )
                )}
              </div>
            </div>
            <div className="col-span-full">
              <Label
                id="sub_ticket.weekly_event_days"
                name="Ticket usage date"
                required
              />
              <Controller
                control={control}
                name="sub_ticket.weekly_event_days"
                defaultValue={[]}
                render={({ field: { onChange, value = [], ref } }) => {
                  const isSelectedAll = arrayCompare(value, [...WEEK_DAYS.map(day => `${day.value}`)]);
                  return (
                    <Select
                      isMulti
                      key="sub_ticket.weekly_event_days"
                      inputRef={ref}
                      options={isSelectedAll ? [] : weekdayArr}
                      id="sub_ticket.weekly_event_days"
                      placeholder="Ticket usage date"
                      value={isSelectedAll ? [ALL_OPTION] : value.map((id: string) => {
                        return WEEK_DAYS.find((item) => item.value === id);
                      })}
                      onChange={(selected: { label: string; value: string }[]) =>
                        selected.find(
                          (item) => item.value === "all"
                        )
                          ? onChange([...WEEK_DAYS.map(day => `${day.value}`)])
                          : onChange(selected.map((item) => `${item.value}`))
                      }
                      error={get(errors,`sub_ticket.weekly_event_days`,"")}
                    />
                  )
                }}
              />
              {get(errors, "sub_ticket.weekly_event_days", "") ? (
                <p className="error-text">
                  <span className="font-medium">
                    {get(errors, `sub_ticket.weekly_event_days.message`, "")}
                  </span>
                </p>
              ) : null}
            </div>
            <div className="col-span-full">
              <Label
                id="sub_ticket.selling_days"
                name="Date of ticket purchase"
                required
              />
              <Controller
                control={control}
                name="sub_ticket.selling_days"
                defaultValue={[]}
                render={({ field: { onChange, value = [], ref } }) => {
                  const isSelectedAll = arrayCompare(value, [...WEEK_DAYS.map(day => `${day.value}`)]);
                  return (
                    <Select
                      isMulti
                      key="sub_ticket.selling_days"
                      inputRef={ref}
                      options={isSelectedAll ? [] : weekdayArr}
                      id="sub_ticket.selling_days"
                      placeholder="Date of ticket purchase"
                      value={isSelectedAll ? [ALL_OPTION] : value.map((id: string) => {
                        return WEEK_DAYS.find((item) => item.value === id);
                      })}
                      onChange={(selected: { label: string; value: string }[]) =>
                        selected.find(
                          (item) => item.value === "all"
                        )
                          ? onChange([...WEEK_DAYS.map(day => `${day.value}`)])
                          : onChange(selected.map((item) => `${item.value}`))
                      }
                      error={get(errors,`sub_ticket.selling_days`,"")}
                    />
                  )
                }}
              />
              {get(errors, "sub_ticket.selling_days", "") ? (
                <p className="error-text">
                  <span className="font-medium">
                    {get(errors, `sub_ticket.selling_days.message`, "")}
                  </span>
                </p>
              ) : null}
            </div>
            <div className="col-span-full">
              <Label
                id="sub_ticket.total_for_sell"
                name="Total for sell per day"
              />
              <Input
                id="sub_ticket.total_for_sell"
                type="number"
                min={0}
                placeholder="Total for sell per day"
                {...register(`sub_ticket.total_for_sell`, {
                  valueAsNumber: true
                })}
                error={get(
                  errors,
                  `sub_ticket.total_for_sell.message`,
                  ""
                )}
              />
              {get(
                errors,
                `sub_ticket.total_for_sell`,
                ""
              ) ? (
                <p className="error-text">
                  <span className="font-medium">
                    {get(
                      errors,
                      `sub_ticket.total_for_sell.message`,
                      ""
                    )}
                  </span>
                </p>
              ) : null}
            </div>
            <div className="col-span-full">
              <Label id="sub_ticket.sold_out_before" name="Sold-out before" />
              <Controller
                control={control}
                name="sub_ticket.sold_out_before"
                render={({ field: { onChange, value = "" } }) => {
                  return (
                    <TimePicker
                      id="sub_ticket.sold_out_before"
                      placeholder="Sold out before"
                      className="bg-white"
                      error={errors["sub_ticket"]?.sold_out_before}
                      value={value}
                      onChange={(date: any) => date === null ? onChange("") : onChange(moment(date).format("HH:mm"))}
                    />
                  );
                }}
              />
              
              {errors["sub_ticket"]?.sold_out_before ? (
                <p className="error-text">
                  <span className="font-medium">
                    {errors["sub_ticket"]?.sold_out_before.message}
                  </span>
                </p>
              ) : null}
            </div>
            
            <div className="col-span-full pb-2 border-b-[1px] border-b-gray-200">
              <p className="text-sm">Price Info</p>
            </div>
            <div className="sm:col-span-3">
              <Label
                id="original_price"
                name="Original price"
                required
              />
              <div className="flex">
                <Input
                  id="original_price"
                  type="number"
                  decimal={2}
                  className="rounded-r-none"
                  placeholder="Original price"
                  disabled={watch("sub_ticket.price_type") === PriceType.FREE}
                  error={get(errors, "sub_ticket.original_price")}
                  {...register("sub_ticket.original_price", {
                    valueAsNumber: true
                  })}  
                />
                <span className="inline-flex items-center rounded-r-md border border-l-0 border-gray-300 bg-gray-100 px-3 text-gray-500 sm:text-sm">
                  {eventDetailData.currency}
                </span>
              </div>
              {get(
                errors,
                `sub_ticket.original_price`,
                ""
              ) ? (
                <p className="error-text">
                  <span className="font-medium">
                    {get(
                      errors,
                      "sub_ticket.original_price.message",
                      ""
                    )}
                  </span>
                </p>
              ) : null}
            </div>
            <div className="sm:col-span-3">
              <Label
                id="price"
                name="Sell price"
                required
              />
              <div className="flex">
                <Input
                  id="price"
                  type="number"
                  decimal={2}
                  className="rounded-r-none"
                  placeholder="Sell price"
                  disabled={watch("sub_ticket.price_type") === PriceType.FREE}
                  error={get(errors, "sub_ticket.price")}
                  {...register("sub_ticket.price", {
                    valueAsNumber: true
                  })}  
                />
                <span className="inline-flex items-center rounded-r-md border border-l-0 border-gray-300 bg-gray-100 px-3 text-gray-500 sm:text-sm">
                  {eventDetailData.currency}
                </span>
              </div>
              {get(
                errors,
                `sub_ticket.price`,
                ""
              ) ? (
                <p className="error-text">
                  <span className="font-medium">
                    {get(
                      errors,
                      "sub_ticket.price.message",
                      ""
                    )}
                  </span>
                </p>
              ) : null}
            </div>
            {/* Pricing by date type UI, only show if frequency_of_sell === USUALLY */}
            {eventDetailData?.date_pricing?.enabled && (
              <div className="col-span-full">
                <div className="grid sm:grid-cols-6 gap-4">
                  <div className="sm:col-span-3">
                    <Label id="pricing_by_date_type.holiday.original_price" name="Holiday Original Price" required />
                    <div className="flex">
                      <Input
                        id="pricing_by_date_type.holiday.original_price"
                        type="number"
                        decimal={2}
                        className="rounded-r-none"
                        placeholder="Holiday original price"
                        disabled={watch("sub_ticket.price_type") === PriceType.FREE}
                        error={errors["sub_ticket"]?.pricing_by_date_type?.holiday?.original_price}
                        {...register("sub_ticket.pricing_by_date_type.holiday.original_price", {
                          valueAsNumber: true
                        })}
                      />
                      <span className="inline-flex items-center rounded-r-md border border-l-0 border-gray-300 bg-gray-100 px-3 text-gray-500 sm:text-sm">
                        {eventDetailData.currency}
                      </span>
                    </div>
                    {errors["sub_ticket"]?.pricing_by_date_type?.holiday?.original_price ? (
                      <p className="error-text">
                        <span className="font-medium">
                          {errors["sub_ticket"]?.pricing_by_date_type?.holiday?.original_price?.message}
                        </span>
                      </p>
                    ) : null}
                  </div>
                  <div className="sm:col-span-3">
                    <Label id="pricing_by_date_type.holiday.price" name="Holiday Price" required />
                    <div className="flex">
                      <Input
                        id="pricing_by_date_type.holiday.price"
                        type="number"
                        decimal={2}
                        className="rounded-r-none"
                        placeholder="Holiday price"
                        disabled={watch("sub_ticket.price_type") === PriceType.FREE}
                        error={errors["sub_ticket"]?.pricing_by_date_type?.holiday?.price}
                        {...register("sub_ticket.pricing_by_date_type.holiday.price", {
                          valueAsNumber: true
                        })}
                      />
                      <span className="inline-flex items-center rounded-r-md border border-l-0 border-gray-300 bg-gray-100 px-3 text-gray-500 sm:text-sm">
                        {eventDetailData.currency}
                      </span>
                    </div>
                    {errors["sub_ticket"]?.pricing_by_date_type?.holiday?.price ? (
                      <p className="error-text">
                        <span className="font-medium">
                          {errors["sub_ticket"]?.pricing_by_date_type?.holiday?.price?.message}
                        </span>
                      </p>
                    ) : null}
                  </div>
                  <div className="sm:col-span-3">
                    <Label id="pricing_by_date_type.weekend.original_price" name="Weekend Original Price" required />
                    <div className="flex">
                      <Input
                        id="pricing_by_date_type.weekend.original_price"
                        type="number"
                        decimal={2}
                        className="rounded-r-none"
                        placeholder="Weekend original price"
                        disabled={watch("sub_ticket.price_type") === PriceType.FREE}
                        error={get(errors, "pricing_by_date_type.weekend.original_price")}
                        {...register("sub_ticket.pricing_by_date_type.weekend.original_price", {
                          valueAsNumber: true
                        })}
                      />
                      <span className="inline-flex items-center rounded-r-md border border-l-0 border-gray-300 bg-gray-100 px-3 text-gray-500 sm:text-sm">
                        {eventDetailData.currency}
                      </span>
                    </div>
                    {errors["sub_ticket"]?.pricing_by_date_type?.weekend?.original_price ? (
                      <p className="error-text">
                        <span className="font-medium">
                          {errors["sub_ticket"]?.pricing_by_date_type?.weekend?.original_price?.message}
                        </span>
                      </p>
                    ) : null}
                  </div>
                  <div className="sm:col-span-3">
                    <Label id="pricing_by_date_type.weekend.price" name="Weekend Price" required />
                    <div className="flex">
                      <Input
                        id="pricing_by_date_type.weekend.price"
                        type="number"
                        decimal={2}
                        className="rounded-r-none"
                        placeholder="Weekend price"
                        disabled={watch("sub_ticket.price_type") === PriceType.FREE}
                        error={errors["sub_ticket"]?.pricing_by_date_type?.weekend?.price}
                        {...register("sub_ticket.pricing_by_date_type.weekend.price", {
                          valueAsNumber: true
                        })}
                      />
                      <span className="inline-flex items-center rounded-r-md border border-l-0 border-gray-300 bg-gray-100 px-3 text-gray-500 sm:text-sm">
                        {eventDetailData.currency}
                      </span>
                    </div>
                    {errors["sub_ticket"]?.pricing_by_date_type?.weekend?.price ? (
                      <p className="error-text">
                        <span className="font-medium">
                          {errors["sub_ticket"]?.pricing_by_date_type?.weekend?.price?.message}
                        </span>
                      </p>
                    ) : null}
                  </div>
                </div>
              </div>
            )}

            <div className="sm:col-span-3">
              <Label
                id="sell_from"
                name="Start date"
                required
              />
              <Controller
                control={control}
                name="sub_ticket.sell_from"
                render={({ field: { onChange, value = "" } }) => {
                  return (
                    <DateTimePicker
                      id="sub_ticket.sell_from"
                      placeholder="Start date"
                      error={get(
                        errors,
                        `sub_ticket.sell_from.message`,
                        ""
                      )}
                      value={value}
                      onChange={(date: any) => date === null ? onChange("") : onChange(moment(date).format("YYYY-MM-DD HH:mm:ss"))}
                    />
                  );
                }}
              />
              {get(
                errors,
                `sub_ticket.sell_from`,
                ""
              ) ? (
                <p className="error-text">
                  <span className="font-medium">
                    {get(
                      errors,
                      `sub_ticket.sell_from.message`,
                      ""
                    )}
                  </span>
                </p>
              ) : null}
            </div>
            <div className="sm:col-span-3">
              <Label
                id="sell_to"
                name="End date"
                required
              />
              <Controller
                control={control}
                name="sub_ticket.sell_to"
                render={({ field: { onChange, value = "" } }) => {
                  return (
                    <DateTimePicker
                      id="sub_ticket.sell_to"
                      placeholder="End date"
                      error={get(
                        errors,
                        `sub_ticket.sell_to.message`,
                        ""
                      )}
                      value={value}
                      onChange={(date: any) => date === null ? onChange("") : onChange(moment(date).format("YYYY-MM-DD HH:mm:ss"))}
                    />
                  );
                }}
              />
              {get(
                errors,
                `sub_ticket.sell_to`,
                ""
              ) ? (
                <p className="error-text">
                  <span className="font-medium">
                    {get(
                      errors,
                      `sub_ticket.sell_to.message`,
                      ""
                    )}
                  </span>
                </p>
              ) : null}
            </div>

            <div className="col-span-full pb-2 border-b-[1px] border-b-gray-200">
              <p className="text-sm">Advance Settings</p>
            </div>
            <div className="col-span-full flex flex-col items-start gap-y-4">
              <Controller
                control={control}
                name="sub_ticket.hide_in_ui"
                render={({ field: { onChange, value, ref } }) => (
                  <SwitchButton label="Show in PROD" checked={!value} onChange={(isChedked) => onChange(!isChedked)} />
                )}
              />
              {get(errors, `sub_ticket.hide_in_ui`, "") ? (
                <p className="error-text">
                  <span className="font-medium">
                    {get(
                      errors,
                      `sub_ticket.hide_in_ui.message`,
                      ""
                    )}
                  </span>
                </p>
              ) : null}
              <Controller
                control={control}
                name="sub_ticket.is_soldout"
                render={({ field: { onChange, value, ref } }) => (
                  <SwitchButton label="Is it Sold Out ?" checked={value} onChange={onChange} />
                )}
              />
              {get(errors, `sub_ticket.is_soldout`, "") ? (
                <p className="error-text">
                  <span className="font-medium">
                    {get(
                      errors,
                      `sub_ticket.is_soldout.message`,
                      ""
                    )}
                  </span>
                </p>
              ) : null}
              
            </div>
            <div className="col-span-full">
              <Label
                id={`detailed_ticket_information_${eventDetailData.languages[0] ?? "en"}`}
                name="Detailed ticket information"
              />
              <div className="flex flex-col gap-y-4">
                {eventDetailData.languages.map(
                  (lang: SupportedLanguage) => (
                    <div key={lang}>
                      <div
                        className={`w-fit flex flex-row items-center gap-x-1 p-2 -mb-1 rounded-tl-md rounded-tr-md border-magnolia-600 border-solid border-[1px] bg-magnolia-400 font-medium text-sm`}
                      >
                        <img
                          src={`/images/flags/circle-${lang}.png`}
                          alt="Language Icon"
                          className="w-4 shrink-0"
                        />
                        <p>
                          {
                            LANGUAGES.find(
                              (language) =>
                                language.value === lang
                            )?.label
                          }
                        </p>
                      </div>
  
                      <Controller
                        control={control}
                        name={`sub_ticket.detailed_ticket_information.${lang}`}
                        render={({
                          field: { onChange, value },
                        }) => (
                          <TextEditor
                            key={`detailed_ticket_information.${lang}`}
                            value={value ?? ""}
                            onChange={onChange}
                          />
                        )}
                      />
  
                      {get(
                        errors,
                        `sub_ticket.detailed_ticket_information.${lang}`,
                        ""
                      ) ? (
                        <p className="error-text">
                          <span className="font-medium">
                            {get(
                              errors,
                              `sub_ticket.detailed_ticket_information.${lang}.message`,
                              ""
                            )}
                          </span>
                        </p>
                      ) : null}
                    </div>
                  )
                )}
              </div>
            </div>
            
          </div>
        ): <></>}
      </div>

    </RightSideModal>
  )
}